import React, { useState, useEffect } from "react";
import { hasStoredAuthToken, useAuth } from "./AuthContext"; // Import corect
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { buildApiUrl } from "./apiConfig";

const DEFAULT_AUTHENTICATED_PATH = "/";

const getAuthenticatedRedirectPath = (locationState) => {
  const from = locationState?.from;

  if (!from || from.pathname === "/login") {
    return DEFAULT_AUTHENTICATED_PATH;
  }

  return `${from.pathname || DEFAULT_AUTHENTICATED_PATH}${from.search || ""}${
    from.hash || ""
  }`;
};

const getLoginErrorMessage = (error) => {
  const status = error?.response?.status;

  if (status === 429) {
    return (
      error.response.data?.message ||
      "Prea multe încercări. Te rugăm să încerci mai târziu."
    );
  }

  if (status === 401) {
    return "Date de autentificare invalide.";
  }

  if (status >= 500) {
    return "Serviciul de autentificare nu este disponibil momentan. Te rugăm să încerci din nou.";
  }

  return "Eroare la autentificare. Vă rugăm să încercați mai târziu.";
};

const inputSx = {
  "& .MuiInputBase-root": {
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    borderRadius: "8px",
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.78)",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#fff",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.24)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.48)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d7b56d",
    borderWidth: "1px",
  },
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, setIsAuthenticated } = useAuth(); // Utilizare corectă a hook-ului
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const redirectPath = getAuthenticatedRedirectPath(location.state);

  useEffect(() => {
    if (hasStoredAuthToken()) {
      setIsAuthenticated(true);
      navigate(redirectPath, { replace: true });
    }
  }, [setIsAuthenticated, navigate, redirectPath]);

  const handleLogin = async (event) => {
    event?.preventDefault();
    if (!username.trim() || !password || isSubmitting) return;

    try {
      setError("");
      setIsSubmitting(true);
      const deviceInfo = {
        os: navigator.platform,
        browser: navigator.userAgent,
        timestamp: new Date().toISOString(),
      };

      await sendLoginRequest(username.trim(), password, deviceInfo);
    } catch (error) {
      console.error("Eroare la autentificare:", error);
      setError("Eroare la autentificare. Vă rugăm să încercați mai târziu.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendLoginRequest = async (username, password, deviceInfo) => {
    try {
      const response = await axios.post(
        buildApiUrl("/login"),
        {
          username,
          password,
          rememberMe,
          deviceInfo,
        }
      );

      if (response.status === 200 && response.data?.token) {
        const didLogin = login(response.data.token, { remember: rememberMe });
        if (!didLogin) {
          setError("Sesiunea primită de la server nu este validă.");
          return;
        }

        navigate(redirectPath, { replace: true });
      } else {
        setError("Sesiunea primită de la server nu este validă.");
      }
    } catch (error) {
      setError(getLoginErrorMessage(error));
    }
  };

  const isLoginButtonDisabled = !username.trim() || !password || isSubmitting;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        color: "#fff",
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(120deg, #07111f 0%, #16223a 38%, #302414 67%, #0b1220 100%)",
        backgroundSize: "240% 240%",
        animation: "loginAurora 13s ease-in-out infinite",
        "@keyframes loginAurora": {
          "0%": { backgroundPosition: "0% 50%" },
          "50%": { backgroundPosition: "100% 50%" },
          "100%": { backgroundPosition: "0% 50%" },
        },
        "@keyframes loginSweep": {
          "0%": { transform: "translateX(-45%) skewX(-16deg)", opacity: 0.25 },
          "50%": { opacity: 0.55 },
          "100%": { transform: "translateX(45%) skewX(-16deg)", opacity: 0.25 },
        },
        "@keyframes loginPanelIn": {
          "0%": { opacity: 0, transform: "translateY(18px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "54px 54px",
          maskImage: "linear-gradient(to bottom, transparent, black 18%, black 78%, transparent)",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: "-20%",
          bottom: "-20%",
          left: "20%",
          width: "40%",
          background:
            "linear-gradient(90deg, transparent, rgba(255,255,255,0.16), transparent)",
          filter: "blur(18px)",
          animation: "loginSweep 7s ease-in-out infinite alternate",
        },
      }}
    >
      <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
      <Box
        component="form"
        onSubmit={handleLogin}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: { xs: 3, sm: 4 },
          borderRadius: "8px",
          backgroundColor: "rgba(7, 13, 24, 0.78)",
          border: "1px solid rgba(255, 255, 255, 0.16)",
          boxShadow: "0 24px 70px rgba(0, 0, 0, 0.42)",
          backdropFilter: "blur(18px)",
          animation: "loginPanelIn 520ms ease-out both",
        }}
      >
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="h4"
            sx={{
              color: "#fff",
              fontWeight: 700,
              letterSpacing: 0,
              lineHeight: 1.15,
            }}
          >
            Autentificare
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255, 255, 255, 0.72)", mt: 1 }}
          >
            Acces securizat în backoffice Luxura.
          </Typography>
        </Box>
        <TextField
          label="Nume de utilizator"
          variant="outlined"
          fullWidth
          autoComplete="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          sx={inputSx}
        />
        <TextField
          label="Parolă"
          type="password"
          variant="outlined"
          fullWidth
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={inputSx}
        />
        {error && (
          <Typography
            variant="body2"
            sx={{
              color: "#ffd6d6",
              backgroundColor: "rgba(185, 28, 28, 0.22)",
              border: "1px solid rgba(255, 214, 214, 0.22)",
              borderRadius: "8px",
              px: 1.5,
              py: 1,
            }}
          >
            {error}
          </Typography>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              sx={{
                color: "rgba(255, 255, 255, 0.72)",
                "&.Mui-checked": { color: "#d7b56d" },
              }}
            />
          }
          label="Ține-mă minte"
          sx={{
            color: "rgba(255, 255, 255, 0.82)",
            alignSelf: "flex-start",
            m: 0,
          }}
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          sx={{
            mt: 1,
            minHeight: 48,
            borderRadius: "8px",
            color: "#111827",
            fontWeight: 700,
            textTransform: "none",
            background: "linear-gradient(135deg, #f4d88a 0%, #d7b56d 100%)",
            boxShadow: "0 12px 30px rgba(215, 181, 109, 0.25)",
            "&:hover": {
              background: "linear-gradient(135deg, #ffe3a0 0%, #e3c175 100%)",
            },
            "&.Mui-disabled": {
              color: "rgba(255, 255, 255, 0.45)",
              background: "rgba(255, 255, 255, 0.14)",
            },
          }}
          disabled={isLoginButtonDisabled}
        >
          {isSubmitting ? (
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          ) : (
            "Autentificare"
          )}
        </Button>
      </Box>
      </Container>
    </Box>
  );
};

export default Login;
